/**
 * Add Training Record Page - Step-by-step wizard
 * Step 1: Choose mode (select existing / add new)
 * Step 2a: Browse & select training (active, not deleted)
 * Step 2b: New training form
 * Step 3: Record details (dates, certificate, rating)
 */

import { useState, useEffect, useRef } from 'react'
import { Link, useNavigate, useSearchParams, useLocation } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import toast from 'react-hot-toast'
import { useAuth } from '@/core/auth/AuthContext'
import { learningApi } from '../api/learningApi'
import type { TrainingType, DeliveryMode, CreateEmployeeTrainingInput, AdminCreateEmployeeTrainingInput, TrainingSimilar, TrainingListItem } from '../types/learning.types'

const TRAINING_TYPE_OPTIONS: { value: TrainingType; label: string }[] = [
  { value: 'course', label: 'Course' },
  { value: 'workshop', label: 'Workshop' },
  { value: 'certification', label: 'Certification' },
  { value: 'webinar', label: 'Webinar' },
  { value: 'seminar', label: 'Seminar' },
  { value: 'conference', label: 'Conference' },
  { value: 'other', label: 'Other' },
]

const TRAINING_TYPE_COLORS: Record<TrainingType, string> = {
  course: 'bg-red-100 text-red-700',
  workshop: 'bg-rose-100 text-rose-700',
  certification: 'bg-green-100 text-green-700',
  webinar: 'bg-orange-100 text-orange-700',
  seminar: 'bg-pink-100 text-pink-700',
  conference: 'bg-indigo-100 text-indigo-700',
  other: 'bg-gray-100 text-gray-700',
}

const DELIVERY_MODE_OPTIONS: { value: DeliveryMode; label: string }[] = [
  { value: 'in_person', label: 'In Person' },
  { value: 'online', label: 'Online' },
  { value: 'hybrid', label: 'Hybrid' },
]

const BROWSE_LIMIT = 12

const AddTrainingRecordPage = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const queryClient = useQueryClient()
  const { user } = useAuth()
  const [searchParams] = useSearchParams()
  const preselectedTrainingId = searchParams.get('training')

  // Admin/HR: adding record on behalf of a specific employee
  const forEmployeeId = searchParams.get('for') || undefined
  const forEmployeeName: string | undefined = (location.state as { employeeName?: string } | null)?.employeeName
  const isAdminOrHr = user?.roles?.some(r => ['admin', 'hr'].includes(r.name.toLowerCase()))
  const isForEmployee = !!(forEmployeeId && isAdminOrHr)

  // Wizard step
  const [currentStep, setCurrentStep] = useState<1 | 2 | 3>(1)

  // Mode: 'select' existing or 'new' training
  const [mode, setMode] = useState<'select' | 'new'>('select')

  // Selected training (for 'select' mode)
  const [selectedTrainingId, setSelectedTrainingId] = useState<string>(preselectedTrainingId || '')
  const [selectedTrainingData, setSelectedTrainingData] = useState<TrainingListItem | null>(null)

  // Browse state (step 2 select mode)
  const [browseSearch, setBrowseSearch] = useState('')
  const [debouncedBrowseSearch, setDebouncedBrowseSearch] = useState('')
  const [browsePage, setBrowsePage] = useState(1)

  useEffect(() => {
    const t = setTimeout(() => setDebouncedBrowseSearch(browseSearch), 300)
    return () => clearTimeout(t)
  }, [browseSearch])

  // New training fields
  const [newTrainingName, setNewTrainingName] = useState('')
  const [newTrainingProvider, setNewTrainingProvider] = useState('')
  const [newTrainingCategory, setNewTrainingCategory] = useState('')
  const [newTrainingType, setNewTrainingType] = useState<TrainingType>('course')
  const [newTrainingDescription, setNewTrainingDescription] = useState('')
  const [showAdvancedFields, setShowAdvancedFields] = useState(false)
  const [newTrainingVendorName, setNewTrainingVendorName] = useState('')
  const [newTrainingVendorWebsite, setNewTrainingVendorWebsite] = useState('')
  const [newTrainingLocation, setNewTrainingLocation] = useState('')
  const [newTrainingDeliveryMode, setNewTrainingDeliveryMode] = useState<DeliveryMode>('in_person')
  const [newTrainingContactPerson, setNewTrainingContactPerson] = useState('')
  const [newTrainingContactEmail, setNewTrainingContactEmail] = useState('')
  const [newTrainingContactPhone, setNewTrainingContactPhone] = useState('')
  const [newTrainingDurationHours, setNewTrainingDurationHours] = useState('')
  const [newTrainingFee, setNewTrainingFee] = useState('')
  const [newTrainingFeeCurrency, setNewTrainingFeeCurrency] = useState('IDR')
  const [newTrainingMaxParticipants, setNewTrainingMaxParticipants] = useState('')
  const [newTrainingPrerequisites, setNewTrainingPrerequisites] = useState('')
  const [newTrainingTargetAudience, setNewTrainingTargetAudience] = useState('')
  const [newTrainingLearningObjectives, setNewTrainingLearningObjectives] = useState('')
  const [newTrainingSyllabus, setNewTrainingSyllabus] = useState('')

  // Common record fields
  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState('')
  const [certificationDate, setCertificationDate] = useState('')
  const [certificationExpiryDate, setCertificationExpiryDate] = useState('')
  const [rating, setRating] = useState<number>(0)
  const [feedback, setFeedback] = useState('')
  const [certificateFile, setCertificateFile] = useState<File | null>(null)

  // Pending materials for new training
  const [pendingMaterials, setPendingMaterials] = useState<File[]>([])
  const newTrainingMaterialsRef = useRef<HTMLInputElement>(null)

  // Similar trainings warning
  const [similarTrainings, setSimilarTrainings] = useState<TrainingSimilar[]>([])

  // Detect if selected/new training is a certification
  const activeTrainingType = mode === 'select' ? selectedTrainingData?.training_type : newTrainingType
  const isCertification = activeTrainingType === 'certification'

  // Duration / min end date
  const activeDurationHours = mode === 'select'
    ? selectedTrainingData?.duration_hours
    : (newTrainingDurationHours ? parseFloat(newTrainingDurationHours) : undefined)

  const minEndDate = (() => {
    if (!startDate || !activeDurationHours || activeDurationHours <= 0) return startDate
    const minDays = Math.ceil(activeDurationHours / 8)
    const d = new Date(startDate)
    d.setDate(d.getDate() + minDays - 1)
    return d.toISOString().split('T')[0]
  })()

  // Fetch preselected training (skip to step 3 when loaded)
  const { data: preselectedTraining } = useQuery({
    queryKey: ['training', preselectedTrainingId],
    queryFn: () => learningApi.getTraining(preselectedTrainingId!),
    enabled: !!preselectedTrainingId,
  })

  useEffect(() => {
    if (preselectedTraining) {
      setMode('select')
      setSelectedTrainingId(preselectedTraining.id)
      setSelectedTrainingData(preselectedTraining as unknown as TrainingListItem)
      setCurrentStep(3)
    }
  }, [preselectedTraining])

  // Browse trainings (step 2, select mode) — active and not deleted only
  const { data: browseData, isLoading: browseLoading } = useQuery({
    queryKey: ['trainingsBrowse', debouncedBrowseSearch, browsePage],
    queryFn: () => learningApi.getTrainings({
      search: debouncedBrowseSearch || undefined,
      is_active: true,
      limit: BROWSE_LIMIT,
      page: browsePage,
    }),
    enabled: currentStep === 2 && mode === 'select',
  })

  const browseTrainings = browseData?.data || []
  const browsePagination = browseData?.pagination

  // Categories
  const { data: categoriesData } = useQuery({
    queryKey: ['trainingCategories'],
    queryFn: () => learningApi.getCategories(),
  })
  const categories = categoriesData?.data || []

  // Documents for selected training
  const { data: documentsData } = useQuery({
    queryKey: ['trainingDocuments', selectedTrainingId],
    queryFn: () => learningApi.listDocuments(selectedTrainingId),
    enabled: !!selectedTrainingId && mode === 'select',
  })
  const documents = documentsData?.data || []


  const handleNewTrainingMaterialSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (file) setPendingMaterials(prev => [...prev, file])
    if (newTrainingMaterialsRef.current) newTrainingMaterialsRef.current.value = ''
  }

  const removePendingMaterial = (index: number) => {
    setPendingMaterials(prev => prev.filter((_, i) => i !== index))
  }

  // Similar trainings check
  useEffect(() => {
    if (mode === 'new' && newTrainingName.length >= 3) {
      const timer = setTimeout(async () => {
        try {
          const result = await learningApi.findSimilarTrainings(newTrainingName)
          setSimilarTrainings(result.data)
        } catch { setSimilarTrainings([]) }
      }, 500)
      return () => clearTimeout(timer)
    } else {
      setSimilarTrainings([])
    }
  }, [newTrainingName, mode])

  // Create record mutation
  const createMutation = useMutation({
    mutationFn: async (data: CreateEmployeeTrainingInput) => {
      let record
      if (isForEmployee && forEmployeeId) {
        record = await learningApi.createRecordForEmployee({ ...data, employee_id: forEmployeeId } as AdminCreateEmployeeTrainingInput)
      } else {
        record = await learningApi.createRecord(data)
      }
      if (certificateFile) await learningApi.uploadCertificate(record.id, certificateFile)
      if (pendingMaterials.length > 0 && record.training?.id) {
        for (const file of pendingMaterials) {
          try { await learningApi.uploadDocument(record.training.id, file) } catch { /* silent */ }
        }
      }
      return record
    },
    onSuccess: (record) => {
      queryClient.invalidateQueries({ queryKey: ['myTrainingRecords'] })
      queryClient.invalidateQueries({ queryKey: ['myCertifications'] })
      queryClient.invalidateQueries({ queryKey: ['trainings'] })
      queryClient.invalidateQueries({ queryKey: ['employeeRecords', forEmployeeId] })
      queryClient.invalidateQueries({ queryKey: ['employeesOverview'] })
      toast.success('Training record added successfully')
      if (isForEmployee && forEmployeeId) {
        navigate(`/learning/employees/${forEmployeeId}`)
      } else if (record.training?.training_type === 'certification') {
        navigate('/learning/certifications')
      } else {
        navigate('/learning/my-records')
      }
    },
    onError: (error: any) => {
      toast.error(error?.error?.message || 'Failed to add training record')
    },
  })

  const handleSelectTraining = (training: TrainingListItem) => {
    setSelectedTrainingId(training.id)
    setSelectedTrainingData(training)
    setCurrentStep(3)
  }

  const handleUseSimilar = (training: TrainingSimilar) => {
    setMode('select')
    setSelectedTrainingId(training.id)
    setSimilarTrainings([])
    setCurrentStep(3)
  }

  const handleNewTrainingNext = () => {
    if (!newTrainingName.trim()) { toast.error('Please enter a training name'); return }
    if (!newTrainingDurationHours || parseFloat(newTrainingDurationHours) <= 0) {
      toast.error('Please enter a valid duration (greater than 0)')
      return
    }
    setCurrentStep(3)
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!startDate) { toast.error('Please enter a start date'); return }
    if (mode === 'select' && !selectedTrainingId) { toast.error('Please select a training'); return }
    if (mode === 'new' && !newTrainingName) { toast.error('Please enter a training name'); return }
    if (endDate && minEndDate && endDate < minEndDate) {
      const days = Math.ceil((activeDurationHours || 0) / 8)
      toast.error(`End date must be at least ${days} day${days > 1 ? 's' : ''} after start date (${activeDurationHours} hrs at max 8 hrs/day)`)
      return
    }
    if (isCertification && !certificationDate) {
      toast.error('Please enter the certification date')
      return
    }
    if (isCertification && certificationExpiryDate && certificationDate && certificationExpiryDate < certificationDate) {
      toast.error('Expiry date must be after the certification date')
      return
    }

    const data: CreateEmployeeTrainingInput = {
      start_date: startDate,
      end_date: endDate || undefined,
      certification_date: isCertification && certificationDate ? certificationDate : undefined,
      certification_expiry_date: isCertification && certificationExpiryDate ? certificationExpiryDate : undefined,
      rating: rating > 0 ? rating : undefined,
      feedback: feedback || undefined,
    }

    if (mode === 'select') {
      data.training_id = selectedTrainingId
    } else {
      data.new_training = {
        name: newTrainingName,
        provider: newTrainingProvider || undefined,
        category_id: newTrainingCategory || undefined,
        training_type: newTrainingType,
        description: newTrainingDescription || undefined,
        vendor_name: newTrainingVendorName || undefined,
        vendor_website: newTrainingVendorWebsite || undefined,
        location: newTrainingLocation || undefined,
        delivery_mode: newTrainingDeliveryMode,
        contact_person: newTrainingContactPerson || undefined,
        contact_email: newTrainingContactEmail || undefined,
        contact_phone: newTrainingContactPhone || undefined,
        duration_hours: newTrainingDurationHours ? parseFloat(newTrainingDurationHours) : undefined,
        fee: newTrainingFee ? parseFloat(newTrainingFee) : undefined,
        fee_currency: newTrainingFeeCurrency || undefined,
        max_participants: newTrainingMaxParticipants ? parseInt(newTrainingMaxParticipants) : undefined,
        prerequisites: newTrainingPrerequisites || undefined,
        target_audience: newTrainingTargetAudience || undefined,
        learning_objectives: newTrainingLearningObjectives || undefined,
        syllabus: newTrainingSyllabus || undefined,
      }
    }

    createMutation.mutate(data)
  }

  // ── Step labels ──────────────────────────────────────────────────────────
  const stepLabels = mode === 'select'
    ? ['Choose type', 'Select training', 'Add details']
    : ['Choose type', 'Training info', 'Add details']

  // ── Shared header + stepper ───────────────────────────────────────────────
  const maxWidth = currentStep === 2 && mode === 'select' ? 'max-w-5xl' : 'max-w-3xl'

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-red-50/30">
      {/* Hero Header */}
      <div className="bg-gradient-to-r from-red-600 via-red-700 to-rose-700 px-6 py-8 shadow-lg">
        <div className={`${maxWidth} mx-auto`}>
          <div className="flex items-center gap-2 text-red-200 text-sm mb-2">
            <Link to="/learning" className="hover:text-white transition-colors">Learning</Link>
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
            {isForEmployee ? (
              <>
                <Link to="/learning/employees" className="hover:text-white transition-colors">By Employee</Link>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
                <Link to={`/learning/employees/${forEmployeeId}`} className="hover:text-white transition-colors">
                  {forEmployeeName || 'Employee'}
                </Link>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </>
            ) : (
              <>
                <Link to="/learning/my-records" className="hover:text-white transition-colors">My Records</Link>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </>
            )}
            <span className="text-white">Add Training</span>
          </div>
          <h1 className="text-3xl font-bold text-white mb-1">Add Training Record</h1>
          {isForEmployee && (
            <div className="flex items-center gap-2 mb-3">
              <span className="px-2.5 py-1 bg-white/20 rounded-full text-sm text-white font-medium">
                For: {forEmployeeName || forEmployeeId}
              </span>
              <span className="text-red-200 text-xs">Record will be auto-verified</span>
            </div>
          )}
          {!isForEmployee && <div className="mb-4" />}

          {/* Step indicator */}
          <div className="flex items-center gap-0">
            {stepLabels.map((label, i) => {
              const stepNum = (i + 1) as 1 | 2 | 3
              const done = currentStep > stepNum
              const active = currentStep === stepNum
              return (
                <div key={i} className="flex items-center">
                  <div className="flex items-center gap-2">
                    <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold border-2 transition-all ${
                      done ? 'bg-white border-white text-red-700' :
                      active ? 'bg-red-700 border-white text-white' :
                      'bg-transparent border-red-300 text-red-300'
                    }`}>
                      {done ? (
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      ) : stepNum}
                    </div>
                    <span className={`text-sm hidden sm:block ${active ? 'text-white font-medium' : done ? 'text-red-200' : 'text-red-300'}`}>
                      {label}
                    </span>
                  </div>
                  {i < stepLabels.length - 1 && (
                    <div className={`w-8 sm:w-12 h-0.5 mx-2 ${done ? 'bg-white' : 'bg-red-400/40'}`} />
                  )}
                </div>
              )
            })}
          </div>
        </div>
      </div>

      <div className={`${maxWidth} mx-auto px-6 py-8`}>

        {/* ── STEP 1: Choose mode ──────────────────────────────────────────── */}
        {currentStep === 1 && (
          <div>
            <p className="text-center text-gray-500 mb-8">How would you like to add a training record?</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-2xl mx-auto">
              <button
                type="button"
                onClick={() => { setMode('select'); setBrowseSearch(''); setBrowsePage(1); setCurrentStep(2) }}
                className="group p-8 bg-white rounded-2xl shadow-sm border-2 border-gray-100 hover:border-red-400 hover:shadow-md transition-all text-left"
              >
                <div className="w-14 h-14 rounded-2xl bg-red-50 group-hover:bg-red-100 flex items-center justify-center mb-5 transition-colors">
                  <svg className="w-7 h-7 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                  </svg>
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Select from Repository</h3>
                <p className="text-sm text-gray-500">Browse and select a training from the existing catalog</p>
                <div className="mt-4 flex items-center gap-1 text-red-600 text-sm font-medium">
                  Browse trainings
                  <svg className="w-4 h-4 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </div>
              </button>

              <button
                type="button"
                onClick={() => { setMode('new'); setSimilarTrainings([]); setCurrentStep(2) }}
                className="group p-8 bg-white rounded-2xl shadow-sm border-2 border-gray-100 hover:border-red-400 hover:shadow-md transition-all text-left"
              >
                <div className="w-14 h-14 rounded-2xl bg-red-50 group-hover:bg-red-100 flex items-center justify-center mb-5 transition-colors">
                  <svg className="w-7 h-7 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Add New Training</h3>
                <p className="text-sm text-gray-500">Training not in the catalog? Create and log it yourself</p>
                <div className="mt-4 flex items-center gap-1 text-red-600 text-sm font-medium">
                  Create training
                  <svg className="w-4 h-4 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </div>
              </button>
            </div>

            <div className="mt-6 text-center">
              <button
                type="button"
                onClick={() => navigate(isForEmployee && forEmployeeId ? `/learning/employees/${forEmployeeId}` : '/learning/my-records')}
                className="text-sm text-gray-400 hover:text-gray-600 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* ── STEP 2a: Browse & select training ───────────────────────────── */}
        {currentStep === 2 && mode === 'select' && (
          <div>
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5 mb-6">
              <div className="flex items-center gap-3">
                <input
                  type="text"
                  value={browseSearch}
                  onChange={(e) => { setBrowseSearch(e.target.value); setBrowsePage(1) }}
                  placeholder="Search by name, provider..."
                  className="flex-1 px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-transparent focus:bg-white transition-all text-sm"
                  autoFocus
                />
                {browseSearch && (
                  <button
                    type="button"
                    onClick={() => { setBrowseSearch(''); setBrowsePage(1) }}
                    className="p-2.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-xl transition-colors"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                )}
              </div>
            </div>

            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden mb-6">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b border-gray-100">
                    <tr>
                      <th className="px-4 py-3 pl-6 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider w-8">#</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider min-w-[200px]">Training</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider min-w-[120px]">Provider</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider min-w-[120px]">Category</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider min-w-[100px]">Type</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider min-w-[90px]">Duration</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider min-w-[70px]">Rating</th>
                      <th className="px-4 py-3 pr-6 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider min-w-[70px]">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {browseLoading ? (
                      Array.from({ length: 10 }).map((_, i) => (
                        <tr key={i} className="animate-pulse">
                          <td className="px-4 py-3.5 pl-6"><div className="h-4 bg-gray-100 rounded w-5" /></td>
                          <td className="px-4 py-3.5"><div className="h-4 bg-gray-100 rounded w-40" /></td>
                          <td className="px-4 py-3.5"><div className="h-4 bg-gray-100 rounded w-24" /></td>
                          <td className="px-4 py-3.5"><div className="h-5 bg-gray-100 rounded-full w-20" /></td>
                          <td className="px-4 py-3.5"><div className="h-5 bg-gray-100 rounded-full w-18" /></td>
                          <td className="px-4 py-3.5"><div className="h-4 bg-gray-100 rounded w-14" /></td>
                          <td className="px-4 py-3.5"><div className="h-4 bg-gray-100 rounded w-10" /></td>
                          <td className="px-4 py-3.5 pr-6"><div className="h-4 bg-gray-100 rounded w-12" /></td>
                        </tr>
                      ))
                    ) : browseTrainings.length === 0 ? (
                      <tr>
                        <td colSpan={8} className="px-6 py-16 text-center">
                          <svg className="w-12 h-12 mx-auto text-gray-300 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                          </svg>
                          <p className="text-gray-500 font-medium">No trainings found</p>
                          <p className="text-gray-400 text-sm mt-1">
                            {browseSearch ? 'Try a different search term' : 'No active trainings available'}
                          </p>
                        </td>
                      </tr>
                    ) : (
                      browseTrainings.map((training, index) => (
                        <tr
                          key={training.id}
                          className="hover:bg-red-50/40 cursor-pointer transition-colors"
                          onClick={() => handleSelectTraining(training as TrainingListItem)}
                        >
                          <td className="px-4 py-3.5 pl-6 text-sm text-gray-400">
                            {(browsePage - 1) * BROWSE_LIMIT + index + 1}
                          </td>
                          <td className="px-4 py-3.5">
                            <span className="font-medium text-gray-900 text-sm">{training.name}</span>
                          </td>
                          <td className="px-4 py-3.5 text-sm text-gray-600">
                            {training.provider || <span className="text-gray-300">—</span>}
                          </td>
                          <td className="px-4 py-3.5">
                            {training.category
                              ? <span className="px-2.5 py-1 text-xs font-medium rounded-full bg-blue-50 text-blue-700">{training.category.name}</span>
                              : <span className="text-gray-300 text-sm">—</span>}
                          </td>
                          <td className="px-4 py-3.5">
                            <span className={`px-2.5 py-1 text-xs font-medium rounded-full ${TRAINING_TYPE_COLORS[training.training_type as TrainingType]}`}>
                              {training.training_type.charAt(0).toUpperCase() + training.training_type.slice(1)}
                            </span>
                          </td>
                          <td className="px-4 py-3.5 text-sm text-gray-600">
                            {training.duration_hours
                              ? `${training.duration_hours % 1 === 0 ? training.duration_hours : training.duration_hours.toFixed(1)} hrs`
                              : <span className="text-gray-300">—</span>}
                          </td>
                          <td className="px-4 py-3.5">
                            {training.average_rating ? (
                              <div className="flex items-center gap-1">
                                <svg className="w-3.5 h-3.5 text-yellow-400 shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                  <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                                </svg>
                                <span className="text-sm text-gray-700">{training.average_rating.toFixed(1)}</span>
                              </div>
                            ) : <span className="text-gray-300 text-sm">—</span>}
                          </td>
                          <td className="px-4 py-3.5 pr-6">
                            <button
                              type="button"
                              onClick={(e) => { e.stopPropagation(); handleSelectTraining(training as TrainingListItem) }}
                              className="text-red-600 hover:text-red-800 text-sm font-medium transition-colors"
                            >
                              Select
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Pagination */}
            {browsePagination && browsePagination.pages > 1 && (
              <div className="flex items-center justify-center gap-2 mb-6">
                <button
                  onClick={() => setBrowsePage(p => Math.max(1, p - 1))}
                  disabled={browsePage === 1}
                  className="px-4 py-2 text-sm border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  Prev
                </button>
                <span className="text-sm text-gray-500">
                  {browsePage} / {browsePagination.pages}
                </span>
                <button
                  onClick={() => setBrowsePage(p => Math.min(browsePagination.pages, p + 1))}
                  disabled={browsePage >= browsePagination.pages}
                  className="px-4 py-2 text-sm border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  Next
                </button>
              </div>
            )}

            <div className="flex items-center justify-between">
              <button
                type="button"
                onClick={() => setCurrentStep(1)}
                className="flex items-center gap-2 px-5 py-2.5 text-gray-600 font-medium hover:text-gray-900 hover:bg-gray-100 rounded-xl transition-colors"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
                Back
              </button>
              <p className="text-sm text-gray-400">
                {browsePagination ? `${browsePagination.total} training${browsePagination.total !== 1 ? 's' : ''} available` : ''}
              </p>
            </div>
          </div>
        )}

        {/* ── STEP 2b: New training form ───────────────────────────────────── */}
        {currentStep === 2 && mode === 'new' && (
          <div>
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 mb-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-5">Training Information</h2>
              <div className="space-y-4">

                {/* Similar trainings warning */}
                {similarTrainings.length > 0 && (
                  <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl">
                    <div className="flex items-start gap-3">
                      <svg className="w-5 h-5 text-amber-500 mt-0.5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                      </svg>
                      <div>
                        <p className="font-medium text-amber-800">Similar trainings exist</p>
                        <p className="text-sm text-amber-700 mb-2">Did you mean one of these?</p>
                        <div className="space-y-1">
                          {similarTrainings.map((t) => (
                            <button key={t.id} type="button" onClick={() => handleUseSimilar(t)}
                              className="block text-sm text-amber-900 hover:underline">
                              {t.name} {t.provider && `(${t.provider})`}
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Training Name <span className="text-red-500">*</span></label>
                  <input type="text" value={newTrainingName} onChange={(e) => setNewTrainingName(e.target.value)}
                    placeholder="e.g., AWS Solutions Architect Certification"
                    className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-transparent focus:bg-white transition-all" />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Provider / Organizer</label>
                    <input type="text" value={newTrainingProvider} onChange={(e) => setNewTrainingProvider(e.target.value)}
                      placeholder="e.g., Amazon Web Services"
                      className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-transparent focus:bg-white transition-all" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Category</label>
                    <select value={newTrainingCategory} onChange={(e) => setNewTrainingCategory(e.target.value)}
                      className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-red-500 text-gray-900">
                      <option value="">Select category...</option>
                      {categories.map((cat) => <option key={cat.id} value={cat.id}>{cat.name}</option>)}
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Training Type</label>
                  <div className="grid grid-cols-4 gap-2">
                    {TRAINING_TYPE_OPTIONS.map((opt) => (
                      <button key={opt.value} type="button" onClick={() => setNewTrainingType(opt.value)}
                        className={`px-3 py-2 text-sm rounded-lg border transition-all ${
                          newTrainingType === opt.value ? 'border-red-500 bg-red-50 text-red-700' : 'border-gray-200 text-gray-600 hover:border-gray-300'
                        }`}>
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                    <textarea value={newTrainingDescription} onChange={(e) => setNewTrainingDescription(e.target.value)}
                      rows={2} placeholder="Brief description of the training..."
                      className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-transparent focus:bg-white transition-all resize-none" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Duration (hours) <span className="text-red-500">*</span>
                    </label>
                    <input type="number" step="0.5" min="0.5" value={newTrainingDurationHours}
                      onChange={(e) => setNewTrainingDurationHours(e.target.value)} placeholder="e.g., 16"
                      className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-transparent focus:bg-white transition-all" />
                    <p className="mt-1 text-xs text-gray-400">Used for completion tracking</p>
                  </div>
                </div>

                {/* Advanced Fields Toggle */}
                <button type="button" onClick={() => setShowAdvancedFields(!showAdvancedFields)}
                  className="flex items-center gap-2 text-sm text-red-600 hover:text-red-700 font-medium">
                  <svg className={`w-4 h-4 transition-transform ${showAdvancedFields ? 'rotate-180' : ''}`}
                    fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                  {showAdvancedFields ? 'Hide' : 'Show'} Advanced Details
                </button>

                {showAdvancedFields && (
                  <div className="space-y-4 pt-4 border-t border-gray-100">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Delivery Mode</label>
                      <div className="flex gap-2">
                        {DELIVERY_MODE_OPTIONS.map((opt) => (
                          <button key={opt.value} type="button" onClick={() => setNewTrainingDeliveryMode(opt.value)}
                            className={`px-4 py-2 text-sm rounded-lg border transition-all ${
                              newTrainingDeliveryMode === opt.value ? 'border-red-500 bg-red-50 text-red-700' : 'border-gray-200 text-gray-600 hover:border-gray-300'
                            }`}>
                            {opt.label}
                          </button>
                        ))}
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Location</label>
                      <input type="text" value={newTrainingLocation} onChange={(e) => setNewTrainingLocation(e.target.value)}
                        placeholder="e.g., Jakarta Convention Center"
                        className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-transparent focus:bg-white transition-all" />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Fee</label>
                        <input type="number" min="0" value={newTrainingFee} onChange={(e) => setNewTrainingFee(e.target.value)}
                          placeholder="e.g., 5000000"
                          className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-transparent focus:bg-white transition-all" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Currency</label>
                        <select value={newTrainingFeeCurrency} onChange={(e) => setNewTrainingFeeCurrency(e.target.value)}
                          className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-red-500 text-gray-900">
                          <option value="IDR">IDR</option>
                          <option value="USD">USD</option>
                          <option value="SGD">SGD</option>
                          <option value="EUR">EUR</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Max Participants</label>
                        <input type="number" min="1" value={newTrainingMaxParticipants}
                          onChange={(e) => setNewTrainingMaxParticipants(e.target.value)} placeholder="e.g., 30"
                          className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-transparent focus:bg-white transition-all" />
                      </div>
                    </div>
                    <div className="pt-2">
                      <h4 className="text-sm font-medium text-gray-700 mb-3">Vendor Information</h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Vendor Name</label>
                          <input type="text" value={newTrainingVendorName} onChange={(e) => setNewTrainingVendorName(e.target.value)}
                            placeholder="e.g., Training Solutions Inc."
                            className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-transparent focus:bg-white transition-all" />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Vendor Website</label>
                          <input type="url" value={newTrainingVendorWebsite} onChange={(e) => setNewTrainingVendorWebsite(e.target.value)}
                            placeholder="e.g., https://vendor.com"
                            className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-transparent focus:bg-white transition-all" />
                        </div>
                      </div>
                    </div>
                    <div className="pt-2">
                      <h4 className="text-sm font-medium text-gray-700 mb-3">Contact Information</h4>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Contact Person</label>
                          <input type="text" value={newTrainingContactPerson} onChange={(e) => setNewTrainingContactPerson(e.target.value)}
                            placeholder="e.g., John Doe"
                            className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-transparent focus:bg-white transition-all" />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
                          <input type="email" value={newTrainingContactEmail} onChange={(e) => setNewTrainingContactEmail(e.target.value)}
                            placeholder="e.g., contact@vendor.com"
                            className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-transparent focus:bg-white transition-all" />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Phone</label>
                          <input type="tel" value={newTrainingContactPhone} onChange={(e) => setNewTrainingContactPhone(e.target.value)}
                            placeholder="e.g., +62 21 1234567"
                            className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-transparent focus:bg-white transition-all" />
                        </div>
                      </div>
                    </div>
                    <div className="pt-2">
                      <h4 className="text-sm font-medium text-gray-700 mb-3">Learning Details</h4>
                      <div className="space-y-4">
                        {[
                          { label: 'Target Audience', value: newTrainingTargetAudience, setter: setNewTrainingTargetAudience, placeholder: 'Who should attend this training?' },
                          { label: 'Prerequisites', value: newTrainingPrerequisites, setter: setNewTrainingPrerequisites, placeholder: 'What knowledge or experience is required?' },
                          { label: 'Learning Objectives', value: newTrainingLearningObjectives, setter: setNewTrainingLearningObjectives, placeholder: 'What will participants learn?' },
                          { label: 'Syllabus / Topics', value: newTrainingSyllabus, setter: setNewTrainingSyllabus, placeholder: 'List of topics covered...', rows: 3 },
                        ].map((field) => (
                          <div key={field.label}>
                            <label className="block text-sm font-medium text-gray-700 mb-2">{field.label}</label>
                            <textarea value={field.value} onChange={(e) => field.setter(e.target.value)}
                              rows={field.rows || 2} placeholder={field.placeholder}
                              className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-transparent focus:bg-white transition-all resize-none" />
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {/* Materials */}
                <div className="mt-2 p-4 bg-red-50 border border-red-200 rounded-xl">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-medium text-gray-900 flex items-center gap-2">
                      <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      Training Materials
                      {pendingMaterials.length > 0 && (
                        <span className="px-2 py-0.5 text-xs font-medium bg-red-100 text-red-700 rounded-full">{pendingMaterials.length}</span>
                      )}
                    </h3>
                    <button type="button" onClick={() => newTrainingMaterialsRef.current?.click()}
                      className="px-3 py-1.5 text-sm font-medium text-red-700 bg-red-100 hover:bg-red-200 rounded-lg transition-colors flex items-center gap-1.5">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                      </svg>
                      Add Material
                    </button>
                  </div>
                  <input ref={newTrainingMaterialsRef} type="file" className="hidden"
                    onChange={handleNewTrainingMaterialSelect}
                    accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.zip,.jpg,.jpeg,.png" />
                  {pendingMaterials.length > 0 ? (
                    <div className="space-y-2">
                      {pendingMaterials.map((file, index) => (
                        <div key={index} className="flex items-center justify-between p-3 bg-white border border-red-100 rounded-lg">
                          <div className="flex items-center gap-3 min-w-0 flex-1">
                            <div className="w-8 h-8 bg-red-100 rounded-lg flex items-center justify-center shrink-0">
                              <svg className="w-4 h-4 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                              </svg>
                            </div>
                            <div className="min-w-0 flex-1">
                              <p className="text-sm font-medium text-gray-900 truncate">{file.name}</p>
                              <p className="text-xs text-gray-500">{(file.size / 1024).toFixed(1)} KB</p>
                            </div>
                          </div>
                          <button type="button" onClick={() => removePendingMaterial(index)}
                            className="p-1.5 text-red-600 hover:bg-red-100 rounded-lg transition-colors">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-4">
                      <p className="text-sm text-gray-500">Add training materials (slides, handouts, etc.)</p>
                      <p className="text-xs text-gray-400 mt-1">PDF, Word, Excel, PowerPoint, Images, ZIP</p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="flex items-center justify-between">
              <button type="button" onClick={() => setCurrentStep(1)}
                className="flex items-center gap-2 px-5 py-2.5 text-gray-600 font-medium hover:text-gray-900 hover:bg-gray-100 rounded-xl transition-colors">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
                Back
              </button>
              <button type="button" onClick={handleNewTrainingNext}
                className="px-6 py-3 bg-gradient-to-r from-red-600 to-rose-600 text-white font-medium rounded-xl hover:from-red-700 hover:to-rose-700 transition-all flex items-center gap-2 shadow-lg shadow-red-500/30">
                Continue to Details
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>
          </div>
        )}

        {/* ── STEP 3: Record details ───────────────────────────────────────── */}
        {currentStep === 3 && (
          <form onSubmit={handleSubmit}>
            {/* Selected training info banner */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5 mb-6">
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">
                {mode === 'select' ? 'Selected Training' : 'New Training'}
              </p>
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-xl bg-red-100 flex items-center justify-center shrink-0">
                  <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                  </svg>
                </div>
                <div className="min-w-0 flex-1">
                  <p className="font-semibold text-gray-900">
                    {mode === 'select' ? selectedTrainingData?.name : newTrainingName}
                  </p>
                  <p className="text-sm text-gray-500">
                    {mode === 'select' ? selectedTrainingData?.provider : newTrainingProvider}
                  </p>
                  {activeDurationHours && activeDurationHours > 0 && (
                    <div className="mt-2 inline-flex items-center gap-1.5 px-2.5 py-1 bg-blue-50 rounded-lg text-xs text-blue-700">
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 2m6-2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      {activeDurationHours % 1 === 0 ? activeDurationHours : activeDurationHours.toFixed(1)} hrs
                      {activeDurationHours > 8 && ` · ${Math.ceil(activeDurationHours / 8)} days min`}
                    </div>
                  )}
                </div>
                <button type="button" onClick={() => setCurrentStep(2)}
                  className="text-xs text-red-600 hover:underline shrink-0">
                  Change
                </button>
              </div>
            </div>

            {/* Dates */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 mb-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Training Dates</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Start Date <span className="text-red-500">*</span></label>
                  <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)}
                    className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-transparent focus:bg-white transition-all" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    End Date
                    {activeDurationHours && activeDurationHours > 0 && startDate && (
                      <span className="ml-1.5 text-xs text-blue-500 font-normal">(min: {minEndDate})</span>
                    )}
                  </label>
                  <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)}
                    min={minEndDate || startDate}
                    className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-transparent focus:bg-white transition-all" />
                  {activeDurationHours && activeDurationHours > 8 && (
                    <p className="mt-1 text-xs text-gray-400">
                      {activeDurationHours} hrs ÷ 8 hrs/day = {Math.ceil(activeDurationHours / 8)} days minimum
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Certification Details — only shown for certification type */}
            {isCertification && (
              <div className="bg-green-50 rounded-2xl border border-green-200 p-6 mb-6">
                <div className="flex items-center gap-2 mb-4">
                  <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
                  </svg>
                  <h2 className="text-lg font-semibold text-green-900">Certification Details</h2>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-green-800 mb-2">
                      Certification Date <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="date"
                      value={certificationDate}
                      onChange={(e) => setCertificationDate(e.target.value)}
                      className="w-full px-4 py-3 bg-white border border-green-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all"
                    />
                    <p className="mt-1 text-xs text-green-700">Date the certification was issued / awarded</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-green-800 mb-2">Expiry Date</label>
                    <input
                      type="date"
                      value={certificationExpiryDate}
                      onChange={(e) => setCertificationExpiryDate(e.target.value)}
                      min={certificationDate || undefined}
                      className="w-full px-4 py-3 bg-white border border-green-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all"
                    />
                    <p className="mt-1 text-xs text-green-700">Leave blank if certification does not expire</p>
                  </div>
                </div>
              </div>
            )}

            {/* Certificate */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 mb-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Certificate</h2>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Upload Certificate (PDF or Image)</label>
                <input type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={(e) => setCertificateFile(e.target.files?.[0] || null)}
                  className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-transparent focus:bg-white transition-all file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-red-50 file:text-red-700 file:font-medium hover:file:bg-red-100" />
                {certificateFile && (
                  <p className="mt-2 text-sm text-gray-500">Selected: {certificateFile.name}</p>
                )}
              </div>

              {/* Existing documents (select mode) */}
              {mode === 'select' && selectedTrainingId && documents.length > 0 && (
                <div className="mt-4 p-4 bg-gray-50 rounded-xl">
                  <p className="text-sm font-medium text-gray-700 mb-3">Training Materials ({documents.length})</p>
                  <div className="space-y-2">
                    {documents.map((doc) => (
                      <div key={doc.id} className="flex items-center justify-between p-3 bg-white border border-gray-100 rounded-lg">
                        <div className="flex items-center gap-3 min-w-0 flex-1">
                          <svg className="w-4 h-4 text-gray-400 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                          <p className="text-sm text-gray-700 truncate">{doc.original_filename}</p>
                        </div>
                        <button type="button" onClick={async () => {
                          try {
                            const blob = await learningApi.downloadDocument(doc.id)
                            const url = window.URL.createObjectURL(blob)
                            window.open(url, '_blank')
                          } catch { toast.error('Failed to open document') }
                        }} className="p-1.5 text-gray-400 hover:text-gray-700 rounded-lg transition-colors" title="View">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                          </svg>
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Rating & Feedback */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 mb-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Rating & Feedback</h2>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Your Rating</label>
                  <div className="flex items-center gap-2">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <button key={star} type="button" onClick={() => setRating(star === rating ? 0 : star)} className="focus:outline-none">
                        <svg className={`w-8 h-8 transition-colors ${star <= rating ? 'text-yellow-400' : 'text-gray-300 hover:text-yellow-200'}`}
                          fill="currentColor" viewBox="0 0 20 20">
                          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                        </svg>
                      </button>
                    ))}
                    {rating > 0 && <span className="ml-2 text-sm text-gray-500">{rating} out of 5</span>}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Feedback / Review</label>
                  <textarea value={feedback} onChange={(e) => setFeedback(e.target.value)} rows={3}
                    placeholder="Share your experience with this training..."
                    className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-transparent focus:bg-white transition-all resize-none" />
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="flex items-center justify-between">
              <button type="button" onClick={() => setCurrentStep(2)}
                className="flex items-center gap-2 px-5 py-2.5 text-gray-600 font-medium hover:text-gray-900 hover:bg-gray-100 rounded-xl transition-colors">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
                Back
              </button>
              <button type="submit" disabled={createMutation.isPending}
                className="px-6 py-3 bg-gradient-to-r from-red-600 to-rose-600 text-white font-medium rounded-xl hover:from-red-700 hover:to-rose-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center gap-2 shadow-lg shadow-red-500/30">
                {createMutation.isPending ? (
                  <>
                    <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Saving...
                  </>
                ) : (
                  <>
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    Add Training Record
                  </>
                )}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  )
}

export default AddTrainingRecordPage
